import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "@clerk/react";
import { Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useApi, errMsg, euro, STATUS, ymd } from "@/lib/api";
import { useInfo } from "@/context/InfoContext";
import { AddToCalendar } from "@/components/site/AddToCalendar";

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-1 border ${STATUS[status]?.cls}`}>{STATUS[status]?.label}</span>
);

const BookingRow = ({ b, onCancel, upcoming = false }) => (
  <li className="py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`account-booking-${b.code}`}>
    <div>
      <p className="font-semibold">{b.service_name} <span className="font-mono text-sm text-zinc-500 ml-2">{b.code}</span></p>
      <p className="text-sm text-zinc-600">{b.date_label} · {b.start}</p>
      <p className="text-sm text-zinc-500">{euro(b.amount_due)}{b.reward_code ? ` · Recompensa ${b.reward_code}` : ""}{b.points_awarded ? ` · +${b.points} puntos` : ""}</p>
      {upcoming && <div className="mt-2"><AddToCalendar b={b} compact /></div>}
    </div>
    <div className="flex items-center gap-3">
      <StatusBadge status={b.status} testid={`account-booking-status-${b.code}`} />
      {b.can_cancel && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="text-xs underline text-zinc-600 hover:text-red-600" data-testid={`account-cancel-${b.code}`}>Cancelar</button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Cancelar esta cita?</AlertDialogTitle>
              <AlertDialogDescription>{b.service_name} · {b.date_label} a las {b.start}. Esta acción no se puede deshacer.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-dialog-dismiss">Volver</AlertDialogCancel>
              <AlertDialogAction onClick={() => onCancel(b)} data-testid="cancel-dialog-confirm">Sí, cancelar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  </li>
);

const Loyalty = ({ data, onRedeem, busy }) => {
  const pct = Math.min(100, (data.points / data.reward_points) * 100);
  return (
    <div className="border border-zinc-200 p-6 sm:p-8" data-testid="loyalty-card">
      <p className="text-xs tracking-[0.3em] uppercase text-zinc-500">Programa de fidelidad</p>
      <p className="mt-4 font-mono text-5xl" data-testid="client-loyalty-points-counter">{data.points}</p>
      <p className="text-sm text-zinc-500">puntos · 1 punto por cada euro pagado</p>
      <div className="mt-6 h-1 bg-zinc-100"><div className="h-1 bg-[#111111] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <p className="mt-2 text-xs text-zinc-500">{data.points >= data.reward_points ? "¡Ya puedes canjear un corte gratis!" : `Te faltan ${data.reward_points - data.points} puntos para un corte gratis (hasta 15 €).`}</p>
      <button onClick={onRedeem} disabled={busy || data.points < data.reward_points} data-testid="redeem-reward-button" className="mt-6 h-11 px-6 bg-[#111111] text-white text-xs tracking-[0.2em] uppercase disabled:opacity-30">
        Canjear {data.reward_points} puntos
      </button>
      <div className="mt-8">
        <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Mis recompensas</p>
        {data.rewards.length === 0 ? <p className="text-sm text-zinc-400" data-testid="no-rewards">Aún no tienes recompensas.</p> : (
          <ul className="space-y-2">
            {data.rewards.map((r) => (
              <li key={r.id} className="flex justify-between text-sm border-b border-zinc-100 pb-2" data-testid={`reward-${r.code}`}>
                <span><Gift className="w-4 h-4 inline mr-2" /><span className="font-mono">{r.code}</span></span>
                <span className={r.status === "available" ? "text-[#111111] font-semibold" : "text-zinc-400"}>{r.status === "available" ? "Disponible" : "Utilizada"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default function Account() {
  const api = useApi();
  const { user } = useUser();
  const { reloadMe } = useInfo();
  const [bookings, setBookings] = useState(null);
  const [loyal, setLoyal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([api("get", "/me/bookings"), api("get", "/me/rewards")]);
      setBookings(b.bookings);
      setLoyal(r);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const cancel = async (b) => {
    try {
      const r = await api("post", `/me/bookings/${b.id}/cancel`);
      toast.success(r.cancel_email_status === "sent" ? "Cita cancelada. Te hemos enviado un correo." : "Cita cancelada.");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const redeem = async () => {
    setBusy(true);
    try {
      const r = await api("post", "/me/rewards/redeem");
      toast.success(`Recompensa ${r.reward.code} creada`);
      load();
      reloadMe();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="py-32 text-center text-zinc-600" data-testid="account-error">{error} <button onClick={load} className="underline ml-2">Reintentar</button></div>;
  if (!bookings || !loyal) return <div className="py-32 flex justify-center text-zinc-400" data-testid="account-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const today = ymd(new Date());
  const upcoming = bookings.filter((b) => b.date >= today && ["pending", "confirmed"].includes(b.status)).reverse();
  const history = bookings.filter((b) => !upcoming.includes(b));

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20" data-testid="account-page">
      <p className="text-xs tracking-[0.3em] uppercase text-zinc-500">Mi cuenta</p>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3" data-testid="account-greeting">Hola, {user?.firstName || user?.primaryEmailAddress?.emailAddress}</h1>
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-12">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">Próximas citas</h2>
              <Link to="/reservar" className="text-xs tracking-[0.2em] uppercase underline" data-testid="account-new-booking">Nueva reserva</Link>
            </div>
            {upcoming.length === 0 ? <p className="text-sm text-zinc-400 py-4" data-testid="no-upcoming">No tienes citas próximas.</p> : (
              <ul className="divide-y divide-zinc-200 border-y border-zinc-200" data-testid="upcoming-list">{upcoming.map((b) => <BookingRow key={b.id} b={b} onCancel={cancel} upcoming />)}</ul>
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold mb-2">Historial</h2>
            {history.length === 0 ? <p className="text-sm text-zinc-400 py-4" data-testid="no-history">Sin historial todavía.</p> : (
              <ul className="divide-y divide-zinc-200 border-y border-zinc-200" data-testid="history-list">{history.map((b) => <BookingRow key={b.id} b={b} onCancel={cancel} />)}</ul>
            )}
          </div>
        </div>
        <div className="lg:col-span-5"><Loyalty data={loyal} onRedeem={redeem} busy={busy} /></div>
      </div>
    </section>
  );
}
